import { ArrowDropDown, AutoAwesome, Check, ContentCopy, LinkOutlined, OpenInNew } from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import cx from 'classnames';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useState } from 'react';
import { darkTheme, lightTheme } from '~/theme.css';
import { useTheme } from '~/utils/store';

import * as styles from './CopyPage.css';

type Props = {
  markdown: string | undefined;
  className?: string;
  fileId?: string;
  isPublic?: boolean;
};

export const CopyPage = ({ markdown, className, fileId, isPublic }: Props) => {
  const menu = usePopupState({ variant: 'popover' });
  const theme = useTheme();
  const [copied, setCopied] = useState<'markdown' | 'link' | null>(null);

  const handleCopyMarkdown = () => {
    if (!markdown || copied) return;
    if (!navigator.clipboard) return;
    void navigator.clipboard.writeText(markdown).then(() => {
      setCopied('markdown');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleCopyLink = () => {
    menu.close();
    if (copied) return;
    if (!navigator.clipboard) return;
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied('link');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleViewMarkdown = () => {
    if (!markdown) return;
    const blob = new Blob([markdown], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    menu.close();
  };

  if (!markdown) return null;

  return (
    <div className={cx(styles.container, className)}>
      <button type="button" className={styles.button} onClick={handleCopyMarkdown}>
        {copied ? <Check className={styles.icon} /> : <ContentCopy className={styles.icon} />}
        <span className={styles.buttonText}>
          <span className={styles.hiddenText}>Copy page</span>
          <span className={styles.visibleText}>{copied ? 'Copied!' : 'Copy page'}</span>
        </span>
      </button>
      <button type="button" className={styles.dropdownButton} {...bindTrigger(menu)} aria-label="Copy options">
        <ArrowDropDown style={{ fontSize: 18 }} />
      </button>
      <Menu
        {...bindMenu(menu)}
        elevation={6}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transitionDuration={0}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={cx(styles.menu, {
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            style: {
              marginTop: 4,
            },
          },
        }}
      >
        <MenuItem className={styles.menuItem} onClick={handleCopyLink}>
          <div className={styles.menuItemPrimary}>
            <LinkOutlined className={styles.linkIcon} />
            Copy page link
          </div>
          <div className={styles.menuItemSecondary}>Copy the current page URL to clipboard</div>
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          onClick={isPublic && fileId ? () => menu.close() : handleViewMarkdown}
          component={isPublic && fileId ? 'a' : 'li'}
          href={isPublic && fileId ? `/api/md/${fileId}` : undefined}
          target={isPublic && fileId ? '_blank' : undefined}
        >
          <div className={styles.menuItemPrimary}>
            <OpenInNew className={styles.menuIcon} />
            View Page as Markdown
          </div>
          <div className={styles.menuItemSecondary}>Open the Markdown file in a new tab</div>
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          component="a"
          href={`https://aistudio.google.com/prompts/new_chat?prompt=${encodeURIComponent(markdown)}`}
          target="_blank"
          onClick={() => menu.close()}
        >
          <div className={styles.menuItemPrimary}>
            <AutoAwesome className={styles.menuIcon} />
            Open in Gemini
          </div>
          <div className={styles.menuItemSecondary}>Discuss this page with Google AI Studio</div>
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          component="a"
          href={`https://chatgpt.com/?q=${encodeURIComponent(markdown)}`}
          target="_blank"
          onClick={() => menu.close()}
        >
          <div className={styles.menuItemPrimary}>
            <AutoAwesome className={styles.menuIcon} />
            Open in ChatGPT
          </div>
          <div className={styles.menuItemSecondary}>Discuss this page with ChatGPT</div>
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          component="a"
          href={`https://claude.ai/new?q=${encodeURIComponent(markdown)}`}
          target="_blank"
          onClick={() => menu.close()}
        >
          <div className={styles.menuItemPrimary}>
            <AutoAwesome className={styles.menuIcon} />
            Open in Claude
          </div>
          <div className={styles.menuItemSecondary}>Discuss this page with Claude</div>
        </MenuItem>
      </Menu>
    </div>
  );
};
